import React from 'react';
import { useTranslation } from 'react-i18next';
import { getStoreUrl, getIOSUrl, getAndroidUrl, getMacUrl } from '../utils/storeUrls';
import VideoModal from './VideoModal';

const barHeights = [10, 22, 36, 18, 46, 30, 14, 40, 26, 52, 22, 36, 18, 28, 12, 40, 24, 32, 18, 30, 14, 38, 22, 14, 26];

const sidebarNotes = [
  { icon: '●', title: 'Q2 Roadmap sync',   meta: 'Recording · 04:21',   active: true  },
  { icon: 'M', title: 'Call with Mei',      meta: 'Yesterday · 14 min',  active: false },
  { icon: 'S', title: 'Morning standup',    meta: 'Tue · 8 min · ES→EN', active: false },
  { icon: 'I', title: 'Interview — Priya',  meta: 'Mon · 42 min',        active: false },
  { icon: 'L', title: 'Lecture — Econ 201', meta: 'Mar 14 · 58 min',     active: false },
];

const transcriptLines = [
  { speaker: 'MEI', text: 'If we pull the API deadline forward, we can demo it at ', keyword: 'All-Hands on the 22nd', rest: '. I want numbers from the beta cohort.' },
  { speaker: 'YOU', text: "Agreed. I'll ping Ravi for last week's retention.", keyword: '', rest: '' },
  { speaker: 'MEI', text: 'Bullets, obviously. Everyone skims.', keyword: '', rest: '' },
];

const summaryBullets = [
  'Pull API deadline forward; demo at All-Hands (Apr 22)',
  'Pull beta retention numbers from Ravi by Friday',
  'Summaries default to bullets — ship toggle later',
];

/* Store badge button (dark pill style matching template) */
const BadgeBtn = ({ href, label, children }: { href: string; label: string; children: React.ReactNode }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '8px',
      padding: '8px 14px', borderRadius: '10px',
      background: 'var(--v2-ink)', color: 'var(--v2-bg)',
      fontSize: '12px', fontWeight: 500, textDecoration: 'none',
      opacity: .9, transition: 'opacity .2s',
    }}
  >
    {children}
  </a>
);

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const storeUrl = getStoreUrl(i18n.language);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [videoLoaded, setVideoLoaded] = React.useState(false);
  const [demoOpen, setDemoOpen] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const sectionRef = React.useRef<HTMLElement>(null);
  const demoWasPlayingRef = React.useRef(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setIsPlaying(true); }
    else          { v.pause(); setIsPlaying(false); }
  };

  React.useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      rafId = requestAnimationFrame(() => {
        const el = sectionRef.current;
        if (!el) return;
        const vh = window.innerHeight;
        const progress = Math.min(window.scrollY / vh, 1);
        const scale = 1 - progress * 0.03;
        const opacity = 1 - progress * 0.15;
        el.style.transform = `scale(${scale})`;
        el.style.opacity = String(opacity);
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
    <section
      ref={sectionRef}
      style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)', position: 'sticky', top: 0, zIndex: 1, overflow: 'hidden', marginTop: '-64px', padding: '180px 0 40px', textAlign: 'center', transformOrigin: 'top center', willChange: 'transform, opacity' }}
    >
      {/* Structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Notely AI',
          applicationCategory: 'ProductivityApplication',
          operatingSystem: 'iOS, Android, macOS',
          description: 'AI-powered note-taking and voice recording app with instant transcription, smart summaries, custom categorization, and cross-device synchronization.',
        })}
      </script>

      {/* Background video */}
      <video
        ref={videoRef}
        src="/feature-audio.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="none"
        onCanPlay={() => setVideoLoaded(true)}
        onError={() => setVideoLoaded(true)}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', zIndex: 0, pointerEvents: 'none',
          display: demoOpen ? 'none' : 'block',
        }}
        aria-hidden="true"
      />

      {/* Loading spinner — shown until video can play */}
      {!videoLoaded && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, zIndex: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            className="animate-spin"
            style={{
              width: '40px', height: '40px', borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.2)',
              borderTopColor: 'rgba(255,255,255,0.8)',
            }}
          />
        </div>
      )}

      {/* Dark tint + radial glow overlay */}
      <div
        className="hero-overlay"
        style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: `linear-gradient(to bottom, rgba(11,18,32,0.86) 0%, rgba(11,18,32,0.80) 78%, var(--v2-bg) 100%),
                       radial-gradient(60% 60% at 50% 0%, var(--v2-hero-glow-1) 0%, transparent 60%),
                       radial-gradient(40% 40% at 80% 20%, var(--v2-hero-glow-2) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />

      {/* Edge vignette */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Pause/play button */}
      <button
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause background video' : 'Play background video'}
        style={{
          position: 'absolute', bottom: '24px', right: '28px', zIndex: 4,
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '6px 14px', borderRadius: '999px',
          background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.25)',
          color: '#fff', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
        }}
      >
        {isPlaying ? '⏸' : '▶'} {isPlaying ? 'Pause' : 'Play'}
      </button>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 3 }}>
        {/* Pill badge */}
        <div className="animate-slide-up opacity-0" style={{ animationDelay: '0.1s', marginBottom: '28px' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '6px 14px', borderRadius: '999px',
            background: 'var(--v2-primary-light)', color: 'var(--v2-primary)',
            fontSize: '13px', fontWeight: 600,
          }}>
            <span style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: 'var(--v2-primary)', animation: 'blink 1.2s infinite', flexShrink: 0,
            }} />
            {t('home.hero.pill', 'New · Live translate in 30 languages')}
          </span>
        </div>

        {/* Headline */}
        <h1
          className="animate-slide-up opacity-0"
          style={{
            animationDelay: '0.2s',
            fontSize: 'clamp(40px, 7vw, 80px)',
            fontWeight: 800,
            lineHeight: 1.03,
            letterSpacing: '-.025em',
            color: '#fff',
            maxWidth: '14ch',
            margin: '0 auto 24px',
          }}
        >
          {t('home.hero.titlePart1', 'Never take notes')}{' '}
          <span style={{ position: 'relative', display: 'inline-block', isolation: 'isolate' }}>
            <span style={{
              background: 'linear-gradient(90deg, var(--v2-primary), var(--v2-violet-400))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              {t('home.hero.titleHighlight', 'manually')}
            </span>
            <span style={{
              position: 'absolute', left: 0, right: 0, bottom: '-4px',
              height: '3px', borderRadius: '3px',
              background: 'linear-gradient(90deg, var(--v2-primary), var(--v2-violet-400))',
            }} aria-hidden="true" />
          </span>{' '}
          {t('home.hero.titlePart2', 'again')}
        </h1>

        {/* Lede */}
        <p
          className="animate-slide-up opacity-0"
          style={{
            animationDelay: '0.3s',
            fontSize: 'clamp(17px, 1.7vw, 21px)',
            color: 'rgba(255,255,255,0.75)',
            maxWidth: '56ch',
            margin: '0 auto 32px',
            lineHeight: 1.55,
          }}
        >
          {t('home.hero.subtitle')}
        </p>

        {/* CTA row */}
        <div
          className="animate-slide-up opacity-0"
          style={{
            animationDelay: '0.4s',
            display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap',
            marginBottom: '18px',
          }}
        >
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'var(--v2-primary)', color: '#fff',
              padding: '14px 28px', borderRadius: '999px',
              fontWeight: 600, fontSize: '15px', textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 8px 24px -8px hsl(248 76% 59% / .55)',
              transition: 'transform .15s ease, box-shadow .2s',
            }}
          >
            {t('home.hero.downloadFree')} →
          </a>
          <button
            onClick={() => { demoWasPlayingRef.current = !(videoRef.current?.paused ?? true); setDemoOpen(true); videoRef.current?.pause(); }}
            style={{
              background: 'transparent', color: 'rgba(255,255,255,0.85)',
              padding: '14px 20px', borderRadius: '999px',
              fontWeight: 600, fontSize: '15px', border: 'none', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              transition: 'color .15s',
            }}
          >
            ▶ {t('home.hero.watchDemo', 'Watch demo')}
          </button>
        </div>

        {/* Stat */}
        <div
          className="animate-slide-up opacity-0"
          style={{
            animationDelay: '0.5s', fontSize: '13px', color: 'rgba(255,255,255,0.85)', marginBottom: '20px',
            textShadow: '0 1px 3px rgba(0,0,0,0.5)',
          }}
        >
          {t('home.hero.userStats')}
        </div>

        {/* Store badges */}
        <div
          className="animate-slide-up opacity-0 hidden sm:flex"
          style={{ animationDelay: '0.55s', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '56px' }}
        >
          <BadgeBtn href={getIOSUrl(i18n.language)} label="Download on the App Store">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            App Store
          </BadgeBtn>
          <BadgeBtn href={getAndroidUrl(i18n.language)} label="Get it on Google Play">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            Google Play
          </BadgeBtn>
          <BadgeBtn href={getMacUrl(i18n.language)} label="Download on the Mac App Store">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Mac App Store
          </BadgeBtn>
        </div>

      </div>


    </section>
    {demoOpen && <VideoModal src="/feature-audio_modal.mp4" onClose={() => { setDemoOpen(false); if (demoWasPlayingRef.current) videoRef.current?.play(); }} />}
    </>
  );
};

export default HeroSection;
